import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import { ServicePlanTermFormSchemaValues } from 'src/features/servicePlanTerms/utils/formSchema';
import { CURRENCY } from 'src/types';
import { servicePlanTermsMessages } from 'src/features/servicePlanTerms/utils/messages';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import { PopoverPortal } from '@radix-ui/react-popover';
import { Calendar } from 'src/registry/ui/Calendar';
import React from 'react';
import dayjs from 'dayjs';

type ServicePlanTermFormProps = {
    form: UseFormReturn<ServicePlanTermFormSchemaValues>;
    onSubmit: (values: ServicePlanTermFormSchemaValues) => void;
};

function ServicePlanTermForm({ onSubmit, form }: ServicePlanTermFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);
    const [openValidToCalendar, setOpenValidToCalendar] = React.useState<boolean>(false);

    const { valid_from } = form.watch();

    const disableCalendarValidToDate = new Date(valid_from);
    disableCalendarValidToDate.setHours(0, 0, 0, 0);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidFromCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent
                                                className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                                align="end"
                                            >
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidFromCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidToCalendar} onOpenChange={setOpenValidToCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidToCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent
                                                className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                                align="end"
                                            >
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidToCalendar(false);
                                                        }
                                                    }}
                                                    disabled={{
                                                        before: new Date(
                                                            disableCalendarValidToDate.getTime() + 24 * 60 * 60 * 1000,
                                                        ),
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <SelectFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="trial_period_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.trialPeriodDays)}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="unpaid_grace_period_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.unpaidGracePeriodDays)}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="lock_warning_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.lockWarningDays)}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="lock_grace_period_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.lockGracePeriodDays)}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="stop_warning_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.stopWarningDays)}
            />
            <InputFormField<ServicePlanTermFormSchemaValues>
                form={form}
                name="stop_grace_period_days"
                type="number"
                required={false}
                label={formatMessage(servicePlanTermsMessages.stopGracePeriodDays)}
            />
        </Form>
    );
}

export default ServicePlanTermForm;
